import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'
import { Grid, TextField } from '@mui/material'

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'

import {
    MemberSectionType,
    defaultMemberSection,
    memberSectionSchema,
} from '@/api/mem/memberSection'
import {
    memberSectionsQueryOptions,
    useCreateMemberSectionMutation,
    useDeleteMemberSectionMutation,
    useUpdateMemberSectionMutation,
} from '@/api/mem/memberSectionQueryOptions'
import { OrganizationLevelType } from '@/api/organizations'
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions'
import CreateDetailAccordion, {
    useCreateDetail,
} from '@/components/detail/CreateDetailRecord'
import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents'
import useResponsive from '@/components/hooks/useResponsive'
import ScoregCheckbox from '@/components/mui/ScoregCheckbox'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import SelectOrganization from '@/components/select/SelectOrganization'
import SelectSection from '@/components/select/SelectSection'
import { getOrganization } from '@/store/auth/authSlice'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/sections'
)({
    component: MemberComponent,
})

function RenderSectionOrganization(props: GridRenderCellParams<any, object>) {
    return (<>
        <Grid container spacing={0}>
            <Grid size={12}>
                <span>{props.row.section.name}</span>
            </Grid>
            <Grid size={12}>
                <span>{props.row.organization.name}</span>
            </Grid>
        </Grid>
    </>);
}

function MemberComponent() {
    const params = Route.useParams()
    const memberSectionsQuery = useSuspenseQuery(
        memberSectionsQueryOptions(params.memberUuid),
    )
    const memberSections = memberSectionsQuery.data

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('member', params.memberUuid, [
            'MemberSection',
        ]),
    )
    const permissions = permissionsQuery.data

    const createable =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'MemberSection' && p.permissions.some((a) => a === 'create'),
        ).length > 0

    const sessionOrganization = useSelector(getOrganization)

    const { onCreated, onUpdated, onDeleted, onError } =
        useDetailCrudEvents<MemberSectionType>('memberSection')

    const createMemberSectionMutation = useCreateMemberSectionMutation(
        params.memberUuid,
        onCreated,
        onError,
    )
    const updateMemberSectionMutation = useUpdateMemberSectionMutation(
        params.memberUuid,
        onUpdated,
        onError,
    )
    const deleteMemberSectionMutation = useDeleteMemberSectionMutation(
        params.memberUuid,
        onDeleted,
        onError,
    )

    const basicGridColumns = useBasicGridColumns()

    const onSubmit = (data: MemberSectionType) => {
        createMemberSectionMutation.mutate(data)
    }

    const { xsmall, small, medium, large, xlarge } = useResponsive()

    const columns: GridColDef[] = []
    columns.push(basicGridColumns.activeColumn)

    if (xsmall || small || medium) {
        columns.push({
            field: 'section_organization',
            valueGetter: (_value, row) => `${row}`,
            headerName: 'Stufe',
            minWidth: 75,
            renderCell: RenderSectionOrganization,

        })
    }
    if (small) {
        columns.push(basicGridColumns.validPeriodColumn)
    }
    if (large || xlarge) {
        columns.push(
            {
                field: 'section_name',
                valueGetter: (_value, row) => `${row.section.name}`,
                headerName: 'Stufe',
                minWidth: 50,


            },
            basicGridColumns.organizationNameColumn,
        )

        columns.push(
            basicGridColumns.startDateColumn,
            basicGridColumns.endDateColumn,
        )
        columns.push(
            {
                field: 'unknownPeriod',
                headerName: t('property.general.unknownPeriod'),
                type: 'boolean',
                editable: true,
            }
        )
        columns.push(basicGridColumns.descriptionColumn)
    }

    const createDetail = useCreateDetail<MemberSectionType>({
        onSubmit,
        resolver: yupResolver<MemberSectionType, any, any>(memberSectionSchema),
        defaultValues: {
            ...defaultMemberSection,
            member: {
                uuid: params.memberUuid,
            },
            organization: {
                uuid: sessionOrganization.uuid,
            },
        },
        title: t('action.assign', { entity: t('entity.section') }),
    })

    const editGrid = useEditGrid<MemberSectionType>({
        defaultValues: defaultMemberSection,
        updateMutation: updateMemberSectionMutation,
        deleteMutation: deleteMemberSectionMutation,
        records: memberSections,
        columns: columns,
        title: t('entity.section'),
    })

    if (memberSectionsQuery.isLoading || memberSectionsQuery.isFetching) {
        return <LoadProgress />
    }

    return (<>
        {createable && (<CreateDetailAccordion {...createDetail}>
            <Grid container spacing={2}>
                <input
                    type="hidden"
                    defaultValue={params.memberUuid}
                    {...createDetail.form.register('member.uuid')}
                />
                <input
                    type="hidden"
                    defaultValue={params.memberUuid}
                    {...createDetail.form.register('organization.uuid')}
                />
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4
                    }}>
                    <SelectSection control={createDetail.form.control} filter='assignable' name="section.uuid" />
                </Grid>
                {(sessionOrganization.organizationLevel === 'DIVISION' ||
                    sessionOrganization.organizationLevel === 'FEDERATION') && (
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <SelectOrganization control={createDetail.form.control} name="organization.uuid" organizationLevels={[OrganizationLevelType.TROOP]} />
                        </Grid>
                    )}
            </Grid>
            <Grid container spacing={2}>
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4
                    }}>
                    <ScoregDatePicker
                        name="startDate"
                        defaultValue={dayjs(defaultMemberSection.startDate)}
                        disabled={false}
                        control={createDetail.form.control}
                        label={t('property.general.startDate')}
                        maxDate={dayjs(createDetail.form.getValues('endDate'))}
                    />
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4
                    }}>
                    <ScoregDatePicker
                        name="endDate"
                        defaultValue={null}
                        disabled={false}
                        control={createDetail.form.control}
                        label={t('property.general.endDate')}
                        minDate={dayjs(createDetail.form.getValues('startDate'))}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid
                    size={{
                        xs: 12,
                        md: 6,
                        lg: 4
                    }}>
                    <ScoregCheckbox defaultValue={false} name="unknownPeriod" control={createDetail.form.control} label={t('property.general.unknownPeriod')} />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 6
                    }}>
                    <TextField
                        {...createDetail.form.register('description')}
                        label={t('property.general.description')}
                        multiline
                        rows={2} />
                </Grid>
            </Grid>
        </CreateDetailAccordion>)
        }
        <EditGrid {...editGrid} detailLink="/member/$memberUuid/membersection/$uuid" parentUuid={(entity) => entity.member.uuid} />
    </>);
}
